import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Alert,
  Table,
  Progress
} from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const SSPDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [ssp, setSSP] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genSuccess, setGenSuccess] = useState(null);

  useEffect(() => {
    if (id) {
      fetchSSP();
      fetchSections();
    }
  }, [id]);

  const fetchSSP = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.get(`${baseURL}/ssp-studio/ssps/${id}`, {
        withCredentials: true
      });
      setSSP(response.data.data);
    } catch (err) {
      console.error('Error fetching SSP:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  const fetchSections = async () => {
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.get(`${baseURL}/ssp-studio/ssps/${id}/sections`, {
        withCredentials: true
      });
      setSections(response.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching sections:', err);
      setError(err.response?.data?.message || err.message);
      setLoading(false);
    }
  };

  const getCompletionColor = (completion) => {
    const colors = {
      empty: 'secondary',
      draft: 'warning',
      ready: 'success'
    };
    return colors[completion] || 'info';
  };

  const calculateProgress = () => {
    if (sections.length === 0) return 0;
    const completedSections = sections.filter(s => s.completion === 'ready').length;
    return Math.round((completedSections / sections.length) * 100);
  };

  const handleGenerateFromScans = async () => {
    if (!window.confirm('This will auto-generate SSP content from scan findings. Existing content will be overwritten. Continue?')) {
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

      const response = await axios.post(
        `${baseURL}/ssp-studio/ssps/${id}/generate-from-scans`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        setGenSuccess(response.data.data);
        fetchSections();
        setTimeout(() => setGenSuccess(null), 10000);
      } else {
        setError('Failed to generate SSP content from scans');
      }
    } catch (err) {
      console.error('Error generating from scans:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleExportToWord = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.post(
        `${baseURL}/ssp-studio/export/${id}/word`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success && response.data.data.downloadUrl) {
        window.location.href = baseURL.replace('/api', '') + response.data.data.downloadUrl;
      }
    } catch (err) {
      console.error('Error exporting to Word:', err);
      setError(err.response?.data?.message || 'Failed to export SSP');
    }
  };

  if (!ssp) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Row className="mb-4">
        <Col>
          <Button color="secondary" outline size="sm" onClick={() => navigate('/ssp-studio/ssps')}>
            <i className="mdi mdi-arrow-left me-1"></i>
            Back to SSP List
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row className="mb-3">
        <Col>
          <h4>{ssp.name}</h4>
        </Col>
      </Row>

      <Row>
        <Col lg={4}>
          <Card>
            <CardBody>
              <CardTitle className="mb-4">
                <i className="mdi mdi-file-document text-primary me-2"></i>
                SSP Information
              </CardTitle>
              <div className="mb-3">
                <h5>{ssp.name}</h5>
                <Badge color={ssp.status === 'approved' ? 'success' : 'warning'}>
                  {ssp.status}
                </Badge>
              </div>

              <div className="mb-3">
                <small className="text-muted d-block">System Name</small>
                <strong>{ssp.metadata?.systemName || 'Not specified'}</strong>
              </div>

              <div className="mb-3">
                <small className="text-muted d-block">System Owner</small>
                <strong>{ssp.metadata?.systemOwner || 'Not specified'}</strong>
              </div>

              <div className="mb-3">
                <small className="text-muted d-block">ISSO</small>
                <strong>{ssp.metadata?.isso || 'Not specified'}</strong>
              </div>

              <div className="mb-3">
                <small className="text-muted d-block">ISSM</small>
                <strong>{ssp.metadata?.issm || 'Not specified'}</strong>
              </div>

              <div className="mb-3">
                <small className="text-muted d-block">Impact Level</small>
                <Badge color={
                  ssp.metadata?.impactLevel === 'High' ? 'danger' :
                  ssp.metadata?.impactLevel === 'Moderate' ? 'warning' : 'info'
                }>
                  {ssp.metadata?.impactLevel || 'Not specified'}
                </Badge>
              </div>

              <div className="mb-3">
                <small className="text-muted d-block">Hosting Model</small>
                <strong>{ssp.metadata?.hostingModel || 'Not specified'}</strong>
              </div>

              <div className="mb-3">
                <small className="text-muted d-block">Created</small>
                <strong>{new Date(ssp.createdAt).toLocaleDateString()}</strong>
              </div>

              <hr />

              <div className="mb-2">
                <small className="text-muted d-block mb-2">Completion Progress</small>
                <Progress value={calculateProgress()} color="success">
                  {calculateProgress()}%
                </Progress>
              </div>

              <div className="mt-3">
                <Button
                  color="primary"
                  size="sm"
                  block
                  className="mb-2"
                  onClick={handleGenerateFromScans}
                  disabled={generating}
                >
                  <i className={`mdi mdi-${generating ? 'loading mdi-spin' : 'auto-fix'} me-1`}></i>
                  {generating ? 'Generating...' : 'Generate from Scans'}
                </Button>
                <Button
                  color="info"
                  size="sm"
                  block
                  className="mb-2"
                  onClick={() => navigate(`/ssp-studio/ssp/${id}/view-full`)}
                >
                  <i className="mdi mdi-file-document-outline me-1"></i>
                  View Full SSP
                </Button>
                <Button 
                  color="success" 
                  size="sm" 
                  block 
                  className="mb-2"
                  onClick={handleExportToWord}
                >
                  <i className="mdi mdi-download me-1"></i>
                  Export to Word
                </Button>
                <Button
                  color="warning"
                  size="sm"
                  block
                  onClick={() => navigate(`/ssp-studio/ssp/${id}/controls`)}
                >
                  <i className="mdi mdi-shield-check me-1"></i>
                  View Controls
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg={8}>
          {genSuccess && (
            <Alert color="success" toggle={() => setGenSuccess(null)} className="mb-3">
              <h5 className="alert-heading">
                <i className="mdi mdi-check-circle me-2"></i>
                SSP Generated Successfully!
              </h5>
              <hr />
              <div>
                <strong>Results:</strong>
                <ul className="mb-0 mt-2">
                  <li>Findings Processed: {genSuccess.findingsProcessed}</li>
                  <li>Controls Updated: {genSuccess.controlsUpdated}</li>
                  <li>Sections Updated: {genSuccess.sectionsUpdated}</li>
                  <li>Narratives Generated: {genSuccess.narrativesGenerated}</li>
                  {genSuccess.errors > 0 && (
                    <li className="text-warning">Errors: {genSuccess.errors}</li>
                  )}
                </ul>
              </div>
              <div className="mt-3">
                <small className="text-muted">
                  <i className="mdi mdi-information"></i> Review the generated content in the sections below.
                  All content is marked as "draft" for your review.
                </small>
              </div>
            </Alert>
          )}

          {ssp.metadata?.authorizationBoundary && (
            <Card className="mb-3">
              <CardBody>
                <CardTitle>Authorization Boundary</CardTitle>
                <p>{ssp.metadata.authorizationBoundary}</p>
              </CardBody>
            </Card>
          )}

          {ssp.metadata?.systemDescription && (
            <Card className="mb-3">
              <CardBody>
                <CardTitle>System Description</CardTitle>
                <p>{ssp.metadata.systemDescription}</p>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardBody>
              <CardTitle className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    <i className="mdi mdi-format-list-bulleted me-2"></i>
                    SSP Sections ({sections.length})
                  </span>
                  <Badge color="light" className="text-dark">
                    {sections.filter(s => s.completion === 'ready').length} / {sections.length} Complete
                  </Badge>
                </div>
              </CardTitle>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading sections...</span>
                  </div>
                </div>
              ) : (
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: '60%' }}>Section</th>
                      <th>Status</th>
                      <th>Last Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sections.map((section) => (
                      <tr key={section._id}>
                        <td>
                          <div style={{ paddingLeft: `${(section.level_snapshot - 1) * 20}px` }}>
                            <strong>{section.title_snapshot}</strong>
                          </div>
                        </td>
                        <td>
                          <Badge color={getCompletionColor(section.completion)}>
                            {section.completion}
                          </Badge>
                        </td>
                        <td>
                          {section.updatedAt ? new Date(section.updatedAt).toLocaleDateString() : '-'}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            color="primary"
                            outline
                            onClick={() => navigate(`/ssp-studio/ssp/${id}/section/${section._id}`)}
                          >
                            <i className="mdi mdi-pencil"></i> Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default SSPDetail;
